import { Currency, type FinancialPeriod, formulaSchema } from "@inrev/common";
import { DateTime } from "luxon";
import type {
	BooleanCell,
	DerivedFormula,
	FinancialStatementPeriodId,
	FinancialViewCell,
	FinancialViewRow,
	StatementMatrixViewModel,
} from "../../ui/FinancialDataGrid";
import { buildPeriodRecord } from "../../ui/FinancialDataGrid";

/** Identifies parent-row adjustments synthesized from child rollup sums. */
export const ROLLUP_ADJUSTMENT_NOTE = "Rollup";

type MergeContext<TPeriodId extends FinancialStatementPeriodId> = {
	periodIds: TPeriodId[];
	shouldRollup: (row: FinancialViewRow<TPeriodId>) => boolean;
	rollupLiveChildren: (row: FinancialViewRow<TPeriodId>) => FinancialViewRow<TPeriodId>[];
	resolveSubtotalChild: (
		row: FinancialViewRow<TPeriodId>,
		liveChildren: FinancialViewRow<TPeriodId>[],
	) => FinancialViewRow<TPeriodId> | null;
};

const recomputeAllowed = (
	given: number | null,
	adjustment: FinancialPeriod.AdjustedLeaf | null,
): number | null =>
	adjustment !== null ? Currency.add(given ?? Currency.ZERO, adjustment.value) : given;

const buildRollupAdjustment = (sum: Currency): FinancialPeriod.AdjustedLeaf | null => {
	if (sum === Currency.ZERO) return null;
	return {
		value: sum,
		formula: formulaSchema.parse(String(sum)),
		createdByUserId: null,
		createdAt: DateTime.now(),
		note: ROLLUP_ADJUSTMENT_NOTE,
	};
};

const aggregateCellsForPeriod = (
	cells: readonly FinancialViewCell[],
): { givenSum: number | null; adjustment: FinancialPeriod.AdjustedLeaf | null } => {
	const givens: number[] = [];
	const adjustments: Currency[] = [];
	for (const cell of cells) {
		if (cell.given !== null && cell.given !== undefined) givens.push(cell.given);
		if (cell.adjustment !== null) adjustments.push(cell.adjustment.value);
	}
	const givenSum = givens.length === 0 ? null : Currency.sum(givens);
	const adjustmentSum = adjustments.length === 0 ? Currency.ZERO : Currency.sum(adjustments);
	return { givenSum, adjustment: buildRollupAdjustment(adjustmentSum) };
};

const sumPeriodCells = <TPeriodId extends FinancialStatementPeriodId>(
	contributors: FinancialViewRow<TPeriodId>[],
	periodId: TPeriodId,
): { givenSum: number | null; adjustment: FinancialPeriod.AdjustedLeaf | null } =>
	aggregateCellsForPeriod(
		contributors.flatMap((child) => {
			const cell = child.cells[periodId];
			return cell ? [cell] : [];
		}),
	);

const computeRollupCells = <TPeriodId extends FinancialStatementPeriodId>(
	contributors: FinancialViewRow<TPeriodId>[],
	periodIds: TPeriodId[],
): Record<TPeriodId, FinancialViewCell> =>
	buildPeriodRecord(periodIds, (periodId) => {
		const { givenSum, adjustment } = sumPeriodCells(contributors, periodId);
		return {
			given: givenSum,
			adjustment,
			allowed: recomputeAllowed(givenSum, adjustment),
		};
	});

const mergeRow = <TPeriodId extends FinancialStatementPeriodId>(
	row: FinancialViewRow<TPeriodId>,
	ctx: MergeContext<TPeriodId>,
	preserveDerivedDuringMerge: boolean,
): FinancialViewRow<TPeriodId> => {
	if (preserveDerivedDuringMerge && row.derivedFrom) {
		return row;
	}

	if (!ctx.shouldRollup(row)) {
		const mergedChildren =
			row.children !== undefined
				? row.children.map((c) => mergeRow(c, ctx, preserveDerivedDuringMerge))
				: undefined;
		return {
			...row,
			...(mergedChildren !== undefined ? { children: mergedChildren } : {}),
		};
	}

	const liveChildren = ctx
		.rollupLiveChildren(row)
		.map((c) => mergeRow(c, ctx, preserveDerivedDuringMerge));

	const subtotalChild = ctx.resolveSubtotalChild(row, liveChildren);
	const contributors = liveChildren.filter((c) => c.kind !== "subtotal");
	const rollupCells = computeRollupCells(contributors, ctx.periodIds);

	return {
		...row,
		cells: rollupCells,
		children: [
			...contributors,
			...(subtotalChild ? [{ ...subtotalChild, cells: rollupCells }] : []),
		],
	};
};

const buildRowLookup = <TPeriodId extends FinancialStatementPeriodId>(
	rows: FinancialViewRow<TPeriodId>[],
): Map<string, FinancialViewRow<TPeriodId>> => {
	const lookup = new Map<string, FinancialViewRow<TPeriodId>>();
	const visit = (row: FinancialViewRow<TPeriodId>) => {
		lookup.set(row.id, row);
		if (row.children) for (const c of row.children) visit(c);
	};
	for (const r of rows) visit(r);
	return lookup;
};

const nullableEquals = (
	x: number | null | undefined,
	y: number | null | undefined,
): boolean | null => (x != null && y != null ? x === y : null);

const evaluateFormula = <TPeriodId extends FinancialStatementPeriodId>(
	formula: DerivedFormula,
	lookup: Map<string, FinancialViewRow<TPeriodId>>,
	periodIds: TPeriodId[],
): {
	cells: Record<TPeriodId, FinancialViewCell>;
	booleanCells?: Record<TPeriodId, BooleanCell>;
} => {
	if (formula.kind === "sum_of_rows") {
		const cells = buildPeriodRecord(periodIds, (periodId) => {
			const contributorCells = formula.rowIds.flatMap((id) => {
				const cell = lookup.get(id)?.cells[periodId];
				return cell ? [cell] : [];
			});
			const { givenSum, adjustment } = aggregateCellsForPeriod(contributorCells);
			return {
				given: givenSum,
				adjustment,
				allowed: recomputeAllowed(givenSum, adjustment),
			};
		});
		return { cells };
	}

	const [aId, bId] = formula.rowIds;
	const booleanCells = buildPeriodRecord(periodIds, (periodId) => {
		const aCell = lookup.get(aId)?.cells[periodId];
		const bCell = lookup.get(bId)?.cells[periodId];
		return {
			given: nullableEquals(aCell?.given, bCell?.given),
			adjustment: null,
			allowed: nullableEquals(aCell?.allowed, bCell?.allowed),
		};
	});
	const cells = buildPeriodRecord(periodIds, () => ({
		given: null,
		adjustment: null,
		allowed: null,
	}));
	return { cells, booleanCells };
};

const evaluateDerivedRow = <TPeriodId extends FinancialStatementPeriodId>(
	row: FinancialViewRow<TPeriodId>,
	lookup: Map<string, FinancialViewRow<TPeriodId>>,
	periodIds: TPeriodId[],
): FinancialViewRow<TPeriodId> => {
	if (!row.derivedFrom) return row;
	const result = evaluateFormula(row.derivedFrom, lookup, periodIds);
	return {
		...row,
		cells: result.cells,
		...(result.booleanCells ? { booleanCells: result.booleanCells } : {}),
	};
};

export type MergeStatementWithEditsArgs<TPeriodId extends FinancialStatementPeriodId> = {
	viewModel: StatementMatrixViewModel<TPeriodId>;
	shouldRollup: (row: FinancialViewRow<TPeriodId>) => boolean;
	rollupLiveChildren: (row: FinancialViewRow<TPeriodId>) => FinancialViewRow<TPeriodId>[];
	resolveSubtotalChild: (
		row: FinancialViewRow<TPeriodId>,
		liveChildren: FinancialViewRow<TPeriodId>[],
	) => FinancialViewRow<TPeriodId> | null;
	evaluateDerivedRows?: boolean;
};

export const mergeStatementWithEdits = <TPeriodId extends FinancialStatementPeriodId>(
	args: MergeStatementWithEditsArgs<TPeriodId>,
): StatementMatrixViewModel<TPeriodId> => {
	const periodIds = args.viewModel.periods.map((p) => p.id);
	const evaluateDerivedRows = args.evaluateDerivedRows ?? false;
	const ctx: MergeContext<TPeriodId> = {
		periodIds,
		shouldRollup: args.shouldRollup,
		rollupLiveChildren: args.rollupLiveChildren,
		resolveSubtotalChild: args.resolveSubtotalChild,
	};

	const merged = args.viewModel.rows.map((r) => mergeRow(r, ctx, evaluateDerivedRows));

	const derivedCount = evaluateDerivedRows ? merged.filter((r) => r.derivedFrom).length : 0;
	if (derivedCount === 0) {
		return { periods: args.viewModel.periods, rows: merged };
	}

	const lookup = buildRowLookup(merged);
	for (let pass = 0; pass < derivedCount; pass++) {
		for (let i = 0; i < merged.length; i++) {
			const row = merged[i];
			if (!row.derivedFrom) continue;
			const updated = evaluateDerivedRow(row, lookup, periodIds);
			merged[i] = updated;
			lookup.set(updated.id, updated);
		}
	}

	return { periods: args.viewModel.periods, rows: merged };
};
